<script lang="ts">
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
    type TableRowsUpdater,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import InboxWorkflowDefaultBulkActions from "@kontextwerk/control-web-frame/shared/modules/inbox-workflow-default-bulk-actions"
  import type { InboxWorkflowTableSelectionContext } from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
  import AnnouncementChannelToggleCell from "./_modules/announcement-channel-toggle-cell"
  import PostalAnnouncementToggleCell from "./_modules/postal-announcement-toggle-cell"
  import PostalAnnouncementTagCell from "./_modules/postal-announcement-tag-cell"
  import buildRowActions from "./_actions/ContactOrderActions.actions"
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import OrderCell from "./_modules/order-cell"
  import type {
    ContactOrderAppointmentRow,
    ContactOrderChannelToggle,
    ContactOrderDigitalChannel,
    ContactOrderPostalToggleOption,
    ContactOrderPlanningRowPresentation,
    RowCommunicationSelection,
    ContactOrderTableRow,
  } from "../../../../_logic/contact-orders-contract"
  import { contactOrderPlanningRowIsCompleted } from "../../_shared/_logic/customer-information-state"
  import type { BatchTableSelectionChangeDetail } from "../../../../_logic/batch-apply-targets"
  import {
    createStableRowNumberState,
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
  } from "../../../../_logic/stable-row-numbers"

  let {
    columns,
    rows,
    loading,
    readOnly,
    refreshKey,
    disabled,
    selectedIds,
    rowPresentationByEventId,
    getDigitalChannelToggles,
    getCommunicationSelection,
    getConfiguredPostalSelection,
    getPostalOptions,
    isAnnouncementPlanPending,
    onRowsUpdaterReady,
    onSelectionChange,
    onToggleDigitalChannel,
    onSelectPostal,
    onTogglePostal,
    onRetryPreview,
    onEditPostal,
    onDisablePlan,
    onEnablePlan,
    onRemoveRows,
    onResetRows,
    onEditRecipients,
  }: {
    columns: ColumnDef[]
    rows: ContactOrderTableRow[]
    loading: boolean
    readOnly: boolean
    refreshKey: string
    disabled: boolean
    selectedIds: string[]
    rowPresentationByEventId: Record<string, ContactOrderPlanningRowPresentation>
    getDigitalChannelToggles: (row: ContactOrderAppointmentRow) => ContactOrderChannelToggle[]
    getCommunicationSelection: (row: ContactOrderAppointmentRow) => RowCommunicationSelection
    getConfiguredPostalSelection: (
      row: ContactOrderAppointmentRow,
    ) => RowCommunicationSelection["postal"]
    getPostalOptions: (row: ContactOrderAppointmentRow) => ContactOrderPostalToggleOption[]
    isAnnouncementPlanPending: (row: ContactOrderAppointmentRow) => boolean
    onRowsUpdaterReady: (updater: TableRowsUpdater) => void
    onSelectionChange: (selectedIds: string[]) => void
    onToggleDigitalChannel: (
      row: ContactOrderAppointmentRow,
      customerId: string,
      channel: ContactOrderDigitalChannel,
      event?: MouseEvent,
    ) => void
    onSelectPostal: (
      row: ContactOrderAppointmentRow,
      value: string,
      dispatch: "MANUAL" | "AUTOMATIC",
    ) => void
    onTogglePostal: (row: ContactOrderAppointmentRow) => void
    onRetryPreview: (row: ContactOrderAppointmentRow) => void
    onEditPostal: (row: ContactOrderAppointmentRow) => void
    onDisablePlan: (row: ContactOrderAppointmentRow, event?: MouseEvent) => void
    onEnablePlan: (row: ContactOrderAppointmentRow, event?: MouseEvent) => void
    onRemoveRows: (rows: ContactOrderAppointmentRow[]) => void
    onResetRows: (rows: ContactOrderAppointmentRow[]) => void
    onEditRecipients: (row: ContactOrderAppointmentRow, event?: MouseEvent) => void
  } = $props()

  const rowNumberState = createStableRowNumberState()
  const tableColumns = $derived([stableRowNumberColumn(), ...columns])
  const isLoadingRow = (row: DataRow): row is ContactOrderTableRow & { isLoading: true } =>
    "isLoading" in row
  const withProjectedRowNumbers = (sourceRows: ContactOrderTableRow[]) =>
    withStableRowNumbers(sourceRows, rowNumberState).map((row) => {
      if (isLoadingRow(row)) return row

      const projectedNumber = rowPresentationByEventId[row.event.id]?.rowNumber

      return typeof projectedNumber === "number" ? { ...row, rowNumber: projectedNumber } : row
    })
  const numberedRows = $derived(withProjectedRowNumbers(rows))
  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: typeof stableRowNumberSort }).sort

    return getStableSortedRowPage({
      rows: numberedRows,
      columns: tableColumns,
      sort,
      limit,
      offset,
    })
  }

  const handleRowsUpdaterReady = (updater: TableRowsUpdater) => {
    onRowsUpdaterReady((updateRows, options) => {
      updater(
        (currentRows) => withProjectedRowNumbers(updateRows(currentRows) as ContactOrderTableRow[]),
        options,
      )
    })
  }

  const isConfirmationInformationRow = (row: ContactOrderAppointmentRow) =>
    String(row.customerInformationMode ?? "").startsWith("confirmation")
  const presentationFor = (row: ContactOrderAppointmentRow) =>
    rowPresentationByEventId[row.event.id] ?? null
  const rowIsCompleted = (row: ContactOrderAppointmentRow) =>
    contactOrderPlanningRowIsCompleted(row)
  const rowIsDisabled = (row: ContactOrderAppointmentRow) =>
    rowIsCompleted(row) || Boolean(presentationFor(row)?.disabled)
  type RowControlCommand = {
    kind: "remove" | "restore"
    disabled?: boolean
    onClick?: () => void | Promise<void>
  }

  const rowControlsFor = (row: DataRow): RowControlCommand[] => {
    if (isLoadingRow(row)) return []

    const appointment = row as ContactOrderAppointmentRow

    if (rowIsDisabled(appointment)) return []

    const commandDisabled = disabled

    if (appointment.removed) {
      return [
        {
          kind: "restore",
          disabled: commandDisabled,
          onClick: () => onEnablePlan(appointment),
        },
      ]
    }

    return [
      {
        kind: "remove",
        disabled: commandDisabled,
        onClick: () => onDisablePlan(appointment),
      },
    ]
  }

  const rowStateFor = (row: DataRow) => {
    if (isLoadingRow(row)) return { disabled: true, selectable: false }

    const appointment = row as ContactOrderAppointmentRow
    const presentation = presentationFor(appointment)
    const workflowDisabled = rowIsDisabled(appointment)
    const muted = Boolean(appointment.removed || presentation?.muted || workflowDisabled)

    return {
      disabled: workflowDisabled,
      selectable: !workflowDisabled && !appointment.removed && (presentation?.selectable ?? true),
      muted,
      disabledReason: presentation?.disabledReason ?? null,
      mutedReason: appointment.removed
        ? "Diese Zeile wird in diesem Schritt ausgelassen."
        : (presentation?.mutedReason ?? null),
    }
  }
  const selectedAppointmentRows = (rows: DataRow[]) =>
    rows.filter((row): row is ContactOrderAppointmentRow => !isLoadingRow(row))
  const resetSelectedRows = (rows: DataRow[]) => onResetRows(selectedAppointmentRows(rows))
  const removeSelectedRows = (rows: DataRow[]) => onRemoveRows(selectedAppointmentRows(rows))
</script>

<Table
  columns={tableColumns}
  {getRows}
  {refreshKey}
  defaultSort={stableRowNumberSort}
  pageSize={25}
  theme="light"
  mode="table-scroll"
  tableCell={planningCell}
  cardField={planningCell}
  showActions={true}
  rowActions={{
    resolve: (row) =>
      buildTableRowActionSet(
        isLoadingRow(row) || isConfirmationInformationRow(row as ContactOrderAppointmentRow)
          ? []
          : buildRowActions({
              row: row as ContactOrderAppointmentRow,
              disabled:
                readOnly ||
                disabled ||
                rowIsDisabled(row as ContactOrderAppointmentRow) ||
                Boolean((row as ContactOrderAppointmentRow).removed) ||
                isAnnouncementPlanPending(row as ContactOrderAppointmentRow),
              postalEditable:
                getConfiguredPostalSelection(row as ContactOrderAppointmentRow).mode === "PERSONAL",
              onEditRecipients,
              onEditPostal,
            }),
        { primaryId: null, inlineIds: ["edit-digital-recipients", "edit-postal-recipient"] },
      ),
  }}
  rowControls={rowControlsFor}
  getRowState={rowStateFor}
  actionsHeaderLabel="Aktionen"
  setRowsUpdater={handleRowsUpdaterReady}
  enableSelection={!readOnly && !loading}
  {selectedIds}
  batchActionsSurfaceTone="dark"
  onSelectionChange={(detail: BatchTableSelectionChangeDetail) => {
    onSelectionChange(detail.selectedIds)
  }}
>
  {#snippet batchActions(context: InboxWorkflowTableSelectionContext)}
    <InboxWorkflowDefaultBulkActions
      selectedRows={context.selectedRows}
      resetRows={resetSelectedRows}
      removeRows={removeSelectedRows}
      clearSelection={context.clearSelection}
      {readOnly}
      {disabled}
      isRowDisabled={(row) =>
        isLoadingRow(row as DataRow) || rowIsDisabled(row as ContactOrderAppointmentRow)}
    />
  {/snippet}
  {#snippet empty()}
    <div class="empty-row">Keine Termine vorhanden.</div>
  {/snippet}
</Table>

  {#snippet planningCell({ column, row }: { column: ColumnDef; row: DataRow })}
    {@const appointment = row as ContactOrderTableRow}
    {#if "isLoading" in appointment}
      <div
      class:wide={column.id === "digitalAnnouncement"}
      class="loading-cell"
      aria-label="Kundeninformation wird geladen"
    >
        <span class="loading-placeholder"></span>
      </div>
    {:else if column.id === "order"}
      <OrderCell orderTag={appointment.orderTag} />
    {:else if column.id === "postage"}
      {#if !isConfirmationInformationRow(appointment)}
        <PostalAnnouncementToggleCell
        preview={appointment.planningPreview}
        hasContactOrder={Boolean(appointment.event.contact_order_id)}
        options={getPostalOptions(appointment)}
        selection={getConfiguredPostalSelection(appointment)}
        disabled={readOnly || disabled || appointment.removed || rowIsDisabled(appointment)}
        pending={isAnnouncementPlanPending(appointment)}
        onChange={(value, dispatch) => onSelectPostal(appointment, value, dispatch)}
      />
      {/if}
    {:else if column.id === "digitalAnnouncement"}
      <AnnouncementChannelToggleCell
      hasContactOrder={Boolean(appointment.event.contact_order_id)}
      channels={getDigitalChannelToggles(appointment)}
      planningPreviewError={appointment.planningPreviewError ?? null}
      customerInformationMode={appointment.customerInformationMode ?? null}
      customerInformationLabel={appointment.customerInformationLabel ?? null}
      customerInformationDetail={appointment.customerInformationDetail ?? null}
      disabled={readOnly || disabled || appointment.removed || rowIsDisabled(appointment)}
      pending={isAnnouncementPlanPending(appointment)}
      onToggle={(customerId, channel, event) =>
        onToggleDigitalChannel(appointment, customerId, channel, event)}
    />
    {:else if column.id === "postalAnnouncement"}
      {#if !isConfirmationInformationRow(appointment) && !appointment.event.contact_order_id}
      <PostalAnnouncementTagCell
        preview={appointment.planningPreview}
        planningPreviewError={appointment.planningPreviewError}
        objectAddressLines={appointment.addressLines}
        options={getPostalOptions(appointment)}
        selection={getConfiguredPostalSelection(appointment)}
        active={getCommunicationSelection(appointment).postal.mode !== "DISABLED"}
        disabled={readOnly ||
          disabled ||
          Boolean(appointment.removed) ||
          rowIsDisabled(appointment)}
        pending={isAnnouncementPlanPending(appointment)}
        onToggle={() => onTogglePostal(appointment)}
        onRetry={() => onRetryPreview(appointment)}
      />
      {/if}
    {:else}
      {row[column.id]}
    {/if}
  {/snippet}

<style lang="less">
  .empty-row {
    padding: 0.4rem 0;
    color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
  }

  .loading-cell {
    width: min(100%, 11rem);
    padding: 0.2rem 0;

    &.wide {
      width: min(100%, 16rem);
    }
  }

  .loading-placeholder {
    display: block;
    width: 100%;
    height: 0.8rem;
    border-radius: 0;
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--text-invers-100) 7%, transparent),
      color-mix(in srgb, var(--text-invers-100) 14%, transparent),
      color-mix(in srgb, var(--text-invers-100) 7%, transparent)
    );
  }
</style>
